import { colors } from 'packages/common-frontend/colors';
import { hasFailedInput, isOverdue } from '~/backup/shared/servicingPeriods';

/**
 * Where a deal stands for the period it is reporting on, as the Portfolio page
 * shows it. Derived from the deal's servicing periods; nothing is persisted.
 */

type PeriodInput = { name: string; status: string };

type PeriodCycle = {
  approvalStatus: string;
  certificateStatus?: string | null;
  reconcileStatus?: string | null;
};

// Optional-and-nullable to match the generated GraphQL types for nullable fields.
type Period = {
  reportingPeriod: string;
  dueDate?: string | null;
  inputs: PeriodInput[];
  cycle?: PeriodCycle | null;
};

export const DEAL_STATUSES = [
  'certified',
  'inProgress',
  'awaitingReview',
  'awaitingTape',
  'overdue',
  'needsAttention',
] as const;

export type DealStatus = typeof DEAL_STATUSES[number];

type DealStatusDisplay = {
  label: string;
  /** A Font Awesome icon name, rendered by `Icon`. */
  icon: string;
  color: string;
};

export const DEAL_STATUS_DISPLAY: { [S in DealStatus]: DealStatusDisplay } = {
  certified: {
    label: 'Certified',
    icon: 'circle-check',
    color: colors.green[400],
  },
  inProgress: {
    label: 'Uploaded, in progress',
    icon: 'arrows-rotate',
    color: colors.blue[400],
  },
  awaitingReview: {
    label: 'Awaiting review',
    icon: 'hourglass-half',
    color: colors.orange[400],
  },
  awaitingTape: {
    label: 'Awaiting tape',
    icon: 'clock',
    color: colors.gray[500],
  },
  overdue: {
    label: 'Overdue',
    icon: 'circle-exclamation',
    color: colors.red[400],
  },
  needsAttention: {
    label: 'Needs attention',
    icon: 'triangle-exclamation',
    color: colors.red[400],
  },
};

/**
 * The period a deal is reporting on: the newest one that has a cycle, has an
 * upload, or is past due. A period that has ended but is not yet due is not
 * owed, so it does not pull every deal back to "awaiting tape" on the first of
 * the month. Before any period qualifies, the first one scheduled. Periods
 * arrive newest first.
 */
export function reportingPeriod<T extends Period>(
  periods: T[],
  today: string,
): T | null {
  return (
    periods.find(
      (p) =>
        Boolean(p.cycle) ||
        p.inputs.some((i) => i.status !== 'awaiting') ||
        isOverdue(p, today),
    ) ??
    periods[periods.length - 1] ??
    null
  );
}

/**
 * A period's status. A terminal off-ramp (a returned review, a failed tie-out or
 * certificate, a failed upload) is checked first: it needs someone to act, and
 * a cycle can be returned for a reason the automated tie-out passed on. A cycle
 * is certified once its certificate is issued — BUS certificates are pre-signed,
 * so nothing waits on a signature.
 */
export function periodStatus(period: Period, today: string): DealStatus {
  const { cycle } = period;
  if (
    hasFailedInput(period) ||
    cycle?.approvalStatus === 'rejected' ||
    cycle?.reconcileStatus === 'fail' ||
    cycle?.certificateStatus === 'blocked'
  ) {
    return 'needsAttention';
  }
  if (cycle) {
    if (cycle.certificateStatus === 'issued') return 'certified';
    if (cycle.approvalStatus === 'pending') return 'awaitingReview';
    return 'inProgress';
  }
  if (period.inputs.some((i) => i.status !== 'awaiting')) return 'inProgress';
  return isOverdue(period, today) ? 'overdue' : 'awaitingTape';
}
